import { jwtDecode } from "jwt-decode";
import { makeKeyValueMaterializer } from "./materialize.js";
import MonthPeriod from "../../models/forecast/MonthPeriod.js";
import Repository from "./Repository.js";

// number of seconds we add for in case of request delays
const tokenAcceptableDelay = 5;

const configuration = {
	// This variable is private to the file and must not be exported for
	// security reasons.
	token: "",
};

/**
 * Configuration for `repositoryBuilder`.
 *
 * @param {string} [token] - A token for authenticating with APIs
 */
export function setConfiguration({ token } = {}) {
	configuration.token = token;
}

/**
 * Creates a repository for accessing the Computation API.
 *
 * @param {number} [version] - API version number
 * @param {string} route - Route suffix for the API endpoint
 * @param {function} [model] - Model constructor for response materialization
 * @param {object} session - Session object providing country, agency, and company IDs
 * @param {object} [defaultFilters] - Default filters to apply to requests
 * @param {Array} [responseHandlers] - Custom response handlers
 * @return {Repository} A configured repository instance
 */
export function makeComputationApiRepository({
	version: apiVersion = 1,
	route: routeSuffix,
	model,
	session,
	defaultFilters = {},
	responseHandlers,
} = {}) {
	const route = `/computationapi/api/#apiVersionString/Countries/#countryId/Agencies/#agencyId/Companies/#companyId/${routeSuffix}`;

	return makeAuthenticatedRepository({
		route,
		model:
			model &&
			makeKeyValueMaterializer({
				key: MonthPeriod,
				value: model,
			}),
		defaultFilters: {
			apiVersionString: `v${apiVersion}`,
			countryId: session?.getCountryId(),
			agencyId: session?.getAgencyId(),
			companyId: session?.getCompanyId(),
			...defaultFilters,
		},
		responseHandlers,
	});
}

/**
 * Creates a repository for accessing the Referential API.
 *
 * @param {number} [version] - API version number
 * @param {string} route - Route suffix for the API endpoint
 * @param {function} [model] - Model constructor for response materialization
 * @param {object} session - Session object providing country ID
 * @param {boolean} [root] - Whether to use the root-level API route
 * @param {object} [defaultFilters] - Default filters to apply to requests
 * @param {object} [context] - Context for request handling
 * @return {Repository} A configured repository instance
 */
export function makeReferentialApiRepository({
	version: apiVersion = 1,
	route: routeSuffix,
	model,
	session,
	root = false,
	defaultFilters = {},
	context,
} = {}) {
	const route = root
		? `/referentialapi/api/#apiVersionString/${routeSuffix}`
		: `/referentialapi/api/#apiVersionString/Countries/#countryId/${routeSuffix}`;

	let fullDefaultFilters = {
		apiVersionString: `v${apiVersion}`,
	};

	if (!root) {
		fullDefaultFilters.countryId = session?.getCountryReferentialApiId();
	}

	fullDefaultFilters = { ...fullDefaultFilters, ...defaultFilters };

	return makeAuthenticatedRepository({
		route,
		model,
		defaultFilters: fullDefaultFilters,
		context,
	});
}

/**
 * Creates a repository for accessing the Import API.
 *
 * @param {string} route - Route suffix for the API endpoint
 * @param {function} [model] - Model constructor for response materialization
 * @param {object} [defaultFilters] - Default filters to apply to requests
 * @return {Repository} A configured repository instance
 */
export function makeImportApiRepository({
	route: routeSuffix,
	model,
	defaultFilters = {},
} = {}) {
	const route = `/importapi/${routeSuffix}`;

	return makeAuthenticatedRepository({
		route,
		model,
		defaultFilters,
	});
}

/**
 * Creates a repository for accessing the Monitor API, optionally injecting an anti-forgery token
 * into the request headers. If no anti-forgery token is provided, a basic repository is returned.
 *
 * @param {object} spec - Specification for the repository
 * @param {string} [spec.antiForgeryToken] - Anti-forgery token to include in requests
 * @param {Array} [spec.requestHandlers] - Array of request handler functions
 * @param {Array} [spec.responseHandlers] - Array of response handler functions
 * @param {string} spec.route - API route for the repository
 * @param {object} [spec.defaultFilters] - Default filters to apply to requests
 * @param {object} [spec.context] - Context for request handling
 * @return {Repository} A configured repository instance with optional anti-forgery protection
 */
export function makeMonitorRepository(spec) {
	if (!spec.antiForgeryToken) return new Repository(spec);

	return new Repository({
		...spec,
		requestHandlers: [
			(req, next) => {
				req.fetchConfiguration.headers.AntiForgeryToken =
					spec.antiForgeryToken;

				next();
			},

			...(spec.requestHandlers || []),
		],
	});
}

function makeAuthenticatedRepository(spec, onFetchTokenRequest = fetchToken) {
	return new Repository({
		...spec,
		requestHandlers: [
			async (req, next) => {
				let requestToken = configuration.token;

				if (!isTokenValid(requestToken)) {
					requestToken = await onFetchTokenRequest();
				}

				req.fetchConfiguration.headers.Authorization = `Bearer ${requestToken}`;

				next();
			},

			...(spec.requestHandlers || []),
		],
		responseHandlers: [
			{
				test: ({ context = {}, response }) =>
					response.status === 401 &&
					!context.hasAlreadyRequestedToken,
				handle: async ({ context = {}, request }) => {
					await onFetchTokenRequest();

					const newContext = {
						...context,
						hasAlreadyRequestedToken: true,
					};

					return request({ context: newContext });
				},
			},
			...(spec.responseHandlers || []),
		],
	});
}

async function fetchToken() {
	const repository = new Repository({
		route: "/Token",
		responseHandlers: [
			{
				test: /^2../,
				handle: ({ response }) => response.text(),
			},
		],
	});

	configuration.token = await repository.get();

	return configuration.token;
}

function isTokenValid(aToken) {
	if (!aToken || aToken.length < 1) return false;

	const decodedToken = jwtDecode(aToken);
	const tokenExpirationEpoch = decodedToken.exp;
	const nowEpoch = Date.now() / 1000;

	return nowEpoch < tokenExpirationEpoch - tokenAcceptableDelay;
}
